// How a saved WhatsApp template message is shown (transcripts, Inbox preview, customer panel).
//
// A direct template send carries the snapshot recorded at send time (server shared/templateMessage.ts): its
// rendered text is shown as sent. Typed agent text is recorded as freeform since PR #111, so the server never
// reports it as a template, whatever its first characters ("[paid] thanks" stays that text).
//
// A row from before both holds only its body. "[name] p1, p2" MAY be the legacy template envelope (parameters
// joined with ", ", no escaping, no language) — or ordinary typed text that starts with a bracket. A bracket prefix
// alone is not proof, so such a row is shown as a template ONLY when the tenant's template catalog knows that name;
// until then (catalog loading, failed, or the name unknown) it is shown exactly as written. A card for a known
// name also keeps the recorded text after the name visible, so nothing the row holds is hidden. Its text is
// explained only with evidence: exactly one body text for the name (several languages with different text = which
// one was sent is unknown), and the recorded parameter text must split into exactly the definition's parameter
// count with no empty piece (a value that itself contained ", " makes the count differ, so it is never guessed).
// Otherwise the card says the template text is not available; invented text is never shown.

import type { TFunction } from 'i18next';
import type { CatalogTemplate } from '../api/templates';
import type { TemplateCatalogState } from './templateCatalog';

/** What the transcript APIs return about a template message (null on every other row). */
export type TranscriptTemplate =
  | { source: 'direct'; name: string; language: string; params: string[]; renderedBody: string | null }
  | { source: 'legacy'; name: string; paramsText: string };

/** How a bubble shows a template message. */
export interface ChatTemplate {
  name: string;
  state: 'resolved' | 'loading' | 'unavailable';
  text: string | null;
  /** 'sent' = the snapshot recorded when it was sent; 'current_definition' = a legacy row explained by the
   *  template's text as it is today. */
  basis: 'sent' | 'current_definition';
  /** A legacy row's recorded text after the name (its stored values — or the rest of typed text), shown as
   *  recorded so the card never hides what the row holds. Null for snapshots and an empty tail. */
  recorded?: string | null;
}

/** Lowercase bracket markers stored for media/system rows — never template names (mirrors the server). */
const RESERVED_MARKER_NAMES = new Set([
  'audio', 'video', 'image', 'document', 'sticker', 'voice', 'location', 'contacts', 'button', 'interactive',
  'reaction', 'unsupported', 'empty', 'text', 'media', 'file', 'attachment', 'template',
]);
const TEMPLATE_PARAM_MAX_CHARS = 1024;

/** The legacy envelope, strictly: "[name]" or "[name] tail" with a valid, non-reserved template name. */
export function parseLegacyEnvelope(body: string | null | undefined): { name: string; paramsText: string } | null {
  if (!body) return null;
  const m = /^\[([a-z0-9_]{1,512})\](?: ([\s\S]*))?$/.exec(body);
  if (!m || RESERVED_MARKER_NAMES.has(m[1]!)) return null;
  return { name: m[1]!, paramsText: m[2] ?? '' };
}

export function renderTemplate(definitionBody: string, params: readonly string[]): string {
  return definitionBody.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n: string) => params[Number(n) - 1] ?? '');
}

/** The stored parameter text as exactly `count` values, or null when that cannot be known. */
export function splitLegacyParams(text: string, count: number): string[] | null {
  if (count === 0) return text.trim() === '' ? [] : null;
  if (count === 1) return [text];
  const parts = text.split(', ');
  return parts.length === count && parts.every((p) => p.length > 0) ? parts : null;
}

/** A legacy row's text from the catalog, or null when the evidence does not support one. */
export function explainLegacy(name: string, paramsText: string, templates: readonly CatalogTemplate[]): string | null {
  const defs = templates.filter((t) => t.name === name && t.body);
  if (defs.length === 0) return null;
  if (new Set(defs.map((d) => d.body)).size !== 1) return null;
  const def = defs[0]!;
  const params = splitLegacyParams(paramsText, def.paramCount);
  if (!params) return null;
  return renderTemplate(def.body, params.map((p) => p.slice(0, TEMPLATE_PARAM_MAX_CHARS)));
}

/** Whether the tenant's catalog (once loaded) has a template of this name — the evidence a legacy row needs. */
export function catalogKnows(catalog: TemplateCatalogState, name: string): boolean {
  return catalog.status === 'ready' && catalog.templates.some((x) => x.name === name);
}

/** How a bubble shows a template row, or null to show the row's body exactly as written (a legacy row without
 *  evidence that it is a template). */
export function displayTemplate(t: TranscriptTemplate, catalog: TemplateCatalogState): ChatTemplate | null {
  if (t.source === 'direct') {
    return t.renderedBody
      ? { name: t.name, state: 'resolved', text: t.renderedBody, basis: 'sent' }
      : { name: t.name, state: 'unavailable', text: null, basis: 'sent' };
  }
  if (catalog.status !== 'ready' || !catalogKnows(catalog, t.name)) return null;
  const recorded = t.paramsText.trim() ? t.paramsText : null;
  const text = explainLegacy(t.name, t.paramsText, catalog.templates);
  return text !== null
    ? { name: t.name, state: 'resolved', text, basis: 'current_definition', recorded }
    : { name: t.name, state: 'unavailable', text: null, basis: 'current_definition', recorded };
}

/**
 * A one-line preview (Inbox list, customer panel) for a body that is a legacy template envelope, or null to show
 * the body as it is. `fromAgent`: the latest message's sender is an agent (true), someone else (false: never a
 * template), or unknown (null). Either way only a name the catalog knows counts as a template (a bracket prefix
 * alone is not proof); an unexplained one keeps its recorded text after the name.
 */
export function templatePreview(
  body: string | null | undefined,
  catalog: TemplateCatalogState,
  t: TFunction,
  { fromAgent }: { fromAgent: boolean | null },
): string | null {
  const env = parseLegacyEnvelope(body);
  if (!env || fromAgent === false || !catalogKnows(catalog, env.name)) return null;
  const text = catalog.status === 'ready' ? explainLegacy(env.name, env.paramsText, catalog.templates) : null;
  if (text !== null) return text;
  const label = t('chat.template_preview', { name: env.name });
  return env.paramsText.trim() ? `${label} · ${env.paramsText}` : label;
}

/** The Inbox/customer-panel preview of a received contact card ("[contacts]" is the stored marker). */
export function contactsPreview(body: string | null | undefined, t: TFunction): string | null {
  return body === '[contacts]' ? t('chat.contact_preview') : null;
}
